import React, { useState, useEffect } from 'react';
import bookingService from '../services/bookingService';
import { useToast } from './Toast';

function ServiceManager({ cachedServices, adminBusinessId }) {
  const [services, setServices] = useState(cachedServices || []);
  const [loading, setLoading] = useState(!cachedServices);
  const [showAddService, setShowAddService] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (!cachedServices) {
      loadServices();
    }
  }, []);

  const loadServices = async () => {
    try {
      console.log('[ServiceManager] Loading services:', {
        adminBusinessId,
        isAdminMode: !!adminBusinessId
      });
      const data = await bookingService.listServices(adminBusinessId);
      console.log('[ServiceManager] Services loaded:', {
        count: data.length,
        adminBusinessId
      });
      setServices(data);
    } catch (error) {
      console.error('Failed to load services:', error);
    } finally {
      setLoading(false);
    }
  };

  const addService = async (serviceData) => {
    try {
      await bookingService.createService(serviceData);
      loadServices();
      setShowAddService(false);
      showToast('Saved successfully!');
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const updateService = async (serviceId, serviceData) => {
    try {
      await bookingService.updateService(serviceId, serviceData);
      loadServices();
      setEditingServiceId(null);
      showToast('Saved successfully!');
    } catch (error) {
      showToast('Failed to save', 'error');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await bookingService.deleteService(serviceId);
      loadServices();
      showToast('Deleted successfully!');
    } catch (error) {
      showToast('Failed to delete', 'error');
    }
  };

  const exportCSV = async () => {
    try {
      const csv = await bookingService.exportServicesCSV();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'services.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Exported successfully!');
    } catch (error) {
      showToast('Failed to export', 'error');
    }
  };

  const importCSV = async (file) => {
    const confirmed = window.confirm(
      'Import will add new services and update existing ones.\n\n' +
      'How it works:\n' +
      '• Leave service_id blank for new services (ID auto-generated)\n' +
      '• Keep service_id to update existing services\n' +
      '• Services not in CSV will NOT be deleted\n\n' +
      'To delete services, use the delete button.\n\n' +
      'Continue with import?'
    );
    
    if (!confirmed) return;
    
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });
      const result = await bookingService.importServicesCSV(text);
      loadServices();
      showToast(`Imported: ${result.created} created, ${result.updated} updated`);
      if (result.errors.length > 0) {
        console.error('Import errors:', result.errors);
      }
    } catch (error) {
      showToast('Failed to import', 'error');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">Loading services...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-900">Services</h3>
        <div className="flex gap-2">
          <button
            onClick={exportCSV}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-bold"
          >
            Export CSV
          </button>
          <label className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm font-bold cursor-pointer" title="Import adds/updates services. Does not delete.">
            Import CSV
            <input
              type="file"
              accept=".csv,text/csv,text/comma-separated-values"
              onChange={(e) => e.target.files[0] && importCSV(e.target.files[0])}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowAddService(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
          >
            Add Service
          </button>
        </div>
      </div>

      {showAddService && (
        <AddServiceForm 
          onSave={addService} 
          onCancel={() => setShowAddService(false)} 
        />
      )}

      <div className="flex flex-col gap-4">
        {services.map(service => (
          <ServiceCard 
            key={service.service_id} 
            service={service} 
            isEditing={editingServiceId === service.service_id}
            onEdit={() => setEditingServiceId(service.service_id)}
            onSave={(serviceData) => updateService(service.service_id, serviceData)}
            onCancel={() => setEditingServiceId(null)}
            onDelete={() => deleteService(service.service_id)}
          />
        ))}
        
        {services.length === 0 && (
          <div className="text-gray-600 text-center py-8 text-sm">
            No services configured. Add your first service to get started.
          </div>
        )}
      </div>
    </div>
  );
}

const ServiceCard = ({ service, isEditing, onEdit, onSave, onCancel, onDelete }) => {
  if (isEditing) {
    return <EditServiceForm service={service} onSave={onSave} onCancel={onCancel} />;
  }

  return (
    <div className="border border-gray-200 rounded p-4 bg-white">
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium text-gray-900 text-base flex-1">{service.name}</h4>
        <div className="flex items-center gap-2 ml-2">
          <button 
            onClick={onEdit}
            className="text-blue-500 hover:text-blue-700 p-1"
            title="Edit service"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button 
            onClick={onDelete}
            className="text-red-500 hover:text-red-700 p-1"
            title="Delete service"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>
      
      <div className="text-sm text-gray-600 space-y-1">
        <div className="flex flex-wrap gap-4">
          <div><span className="font-medium">Duration:</span> {service.duration_minutes} min</div>
          <div><span className="font-medium">Price:</span> ${service.price}</div>
        </div>
        {service.description && (
          <div className="mt-2">
            <span className="font-medium">Description:</span>
            <div className="bg-blue-50 p-2 rounded mt-1 text-blue-900 text-xs leading-relaxed">
              {service.description}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AddServiceForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    duration_minutes: '',
    price: '',
    description: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      duration_minutes: parseInt(formData.duration_minutes),
      price: parseFloat(formData.price)
    };
    onSave(payload);
  };

  return (
    <div className="border border-gray-200 rounded p-4 bg-gray-50 mb-4">
      <h4 className="text-base font-medium text-gray-900 mb-4">Add New Service</h4>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Service Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Oil Change, Brake Inspection"
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                placeholder="30"
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="29.99"
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of the service"
              rows={3}
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 resize-vertical"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white text-gray-600 border border-gray-200 px-4 py-2 rounded text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
          >
            Create Service
          </button>
        </div>
      </form>
    </div>
  );
};

const EditServiceForm = ({ service, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: service.name,
    duration_minutes: service.duration_minutes,
    price: service.price,
    description: service.description || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      duration_minutes: parseInt(formData.duration_minutes),
      price: parseFloat(formData.price)
    };
    onSave(payload);
  };

  return (
    <div className="border border-gray-200 rounded p-4 bg-gray-50">
      <h4 className="text-base font-medium text-gray-900 mb-4">Edit Service</h4>
        
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Service Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Oil Change, Brake Inspection"
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Duration (minutes)</label>
              <input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
                placeholder="30"
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Price</label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="29.99"
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of the service"
              rows={3}
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900 resize-vertical"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            className="bg-white text-gray-600 border border-gray-200 px-4 py-2 rounded text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-bold"
          >
            Update Service
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceManager;
